import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import otherNewsAPI from '../utils/api/otherNewsAPI';
import { getSubmenusForTitle } from '../utils/menuNewsConfig';
import './AdminOtherNews.css';

const initialFormState = {
  title: '',
  menuTitle: '',
  submenu: '',
  description: '',
  content: '',
  image: '',
  featuredImage: null,
  author: 'Admin',
  tags: '',
  videoUrl: '',
  location: '',
  isPublished: true
};

function AdminOtherNews() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState('');
  const [options, setOptions] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const toastTimerRef = useRef(null);

  const submenuOptions = useMemo(
    () => getSubmenusForTitle(formData.menuTitle),
    [formData.menuTitle]
  );

  const imagePreview = useMemo(() => {
    if (formData.featuredImage instanceof File) {
      return URL.createObjectURL(formData.featuredImage);
    }

    return formData.image || '';
  }, [formData.featuredImage, formData.image]);

  useEffect(() => {
    async function loadPageData() {
      try {
        setPageLoading(true);
        const [optionsResponse, listResponse] = await Promise.all([
          otherNewsAPI.getOptions(),
          otherNewsAPI.getAdminNews()
        ]);

        if (optionsResponse.success) {
          const loadedOptions = optionsResponse.data || [];
          setOptions(loadedOptions);

          if (loadedOptions.length > 0) {
            const firstTitle = loadedOptions[0].title;
            const firstSubmenu = loadedOptions[0].submenus?.[0]?.label || '';

            setFormData((current) => ({
              ...current,
              menuTitle: current.menuTitle || firstTitle,
              submenu: current.submenu || firstSubmenu
            }));
          }
        }

        if (listResponse.success) {
          setNewsList(listResponse.data || []);
        }
      } catch (error) {
        showMessage('डेटा लोड नहीं हो सका', 'error');
      } finally {
        setPageLoading(false);
      }
    }

    loadPageData();
  }, []);

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!(formData.featuredImage instanceof File) || !imagePreview.startsWith('blob:')) {
      return undefined;
    }

    return () => {
      URL.revokeObjectURL(imagePreview);
    };
  }, [formData.featuredImage, imagePreview]);

  const refreshNewsList = async () => {
    const response = await otherNewsAPI.getAdminNews();
    if (response.success) {
      setNewsList(response.data || []);
    }
  };

  const resetForm = () => {
    const firstTitle = options[0]?.title || '';
    const firstSubmenu = options[0]?.submenus?.[0]?.label || '';

    setEditingId('');
    setFormData({
      ...initialFormState,
      menuTitle: firstTitle,
      submenu: firstSubmenu,
      author: 'Admin'
    });
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'menuTitle') {
      const nextSubmenus = getSubmenusForTitle(value);
      setFormData((current) => ({
        ...current,
        menuTitle: value,
        submenu: nextSubmenus[0]?.label || ''
      }));
      return;
    }

    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setFormData((current) => ({
      ...current,
      featuredImage: file
    }));
  };

  const buildSubmitData = () => {
    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('menuTitle', formData.menuTitle);
    submitData.append('submenu', formData.submenu);
    submitData.append('description', formData.description);
    submitData.append('content', formData.content);
    submitData.append('image', formData.image || '');
    submitData.append('author', formData.author);
    submitData.append('tags', formData.tags || '');
    submitData.append('videoUrl', formData.videoUrl || '');
    submitData.append('location', formData.location || '');
    submitData.append('isPublished', String(formData.isPublished));

    if (formData.featuredImage instanceof File) {
      submitData.append('featuredImage', formData.featuredImage);
    }

    return submitData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const submitData = buildSubmitData();
      const response = editingId
        ? await otherNewsAPI.updateNews(editingId, submitData)
        : await otherNewsAPI.addNews(submitData);

      if (!response.success) {
        showMessage(response.message || 'समाचार सेव नहीं हो सका', 'error');
        return;
      }

      showMessage(editingId ? 'समाचार सफलतापूर्वक अपडेट कर दिया गया' : 'समाचार सफलतापूर्वक जोड़ दिया गया');
      resetForm();
      await refreshNewsList();
    } catch (error) {
      showMessage('समाचार सेव नहीं हो सका', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || '',
      menuTitle: item.menuTitle || options[0]?.title || '',
      submenu: item.submenu || getSubmenusForTitle(item.menuTitle || options[0]?.title || '')[0]?.label || '',
      description: item.description || '',
      content: item.content || '',
      image: item.image || '',
      featuredImage: null,
      author: item.author || 'Admin',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : item.tags || '',
      videoUrl: item.videoUrl || '',
      location: item.location || '',
      isPublished: item.isPublished !== false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('क्या आप यह समाचार हटाना चाहते हैं?');
    if (!confirmed) {
      return;
    }

    const response = await otherNewsAPI.deleteNews(id);
    if (response.success) {
      showMessage('समाचार हटा दिया गया');
      await refreshNewsList();
      return;
    }

    showMessage(response.message || 'समाचार हटाया नहीं जा सका', 'error');
  };

  return (
    <AdminShell
      eyebrow="Menu Driven News"
      title="Manage other news"
      description="Create secondary feed stories mapped to navbar titles and submenus, while keeping the saved list easy to review and maintain."
      primaryAction={{ label: 'Refresh list', onClick: refreshNewsList }}
      secondaryAction={{ label: 'Dashboard', onClick: () => navigate('/admin/dashboard') }}
      meta={[
        { label: 'Titles', value: String(options.length) },
        { label: 'Saved items', value: String(newsList.length) }
      ]}
    >
      <div className="admin-other-news-page">
        {message && <div className={`admin-message ${messageType}`}>{message}</div>}

        <div className="admin-other-news-grid">
          <section className="admin-card">
          <h2>{editingId ? 'समाचार संपादित करें' : 'नया समाचार जोड़ें'}</h2>
          <form onSubmit={handleSubmit} className="admin-other-news-form">
            <div className="form-row two-column">
              <label>
                <span>Title</span>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </label>
              <label>
                <span>Author</span>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                />
              </label>
            </div>

            <div className="form-row two-column">
              <label>
                <span>First: Title Category</span>
                <select name="menuTitle" value={formData.menuTitle} onChange={handleInputChange} required>
                  {options.map((option) => (
                    <option key={option.title} value={option.title}>
                      {option.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Second: Submenu</span>
                <select name="submenu" value={formData.submenu} onChange={handleInputChange} required>
                  {submenuOptions.map((submenu) => (
                    <option key={submenu.label} value={submenu.label}>
                      {submenu.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label>
              <span>Description</span>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                required
              />
            </label>

            <label>
              <span>Content</span>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows={7}
                required
              />
            </label>

            <div className="form-row two-column">
              <label>
                <span>Upload Image</span>
                <input
                  type="file"
                  name="featuredImage"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </label>
              <label>
                <span>Video URL</span>
                <input
                  type="url"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/..."
                />
              </label>
            </div>

            {imagePreview ? (
              <label>
                <span>Preview</span>
                <img
                  src={imagePreview}
                  alt="Other news preview"
                  style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '12px' }}
                />
              </label>
            ) : null}

            <div className="form-row two-column">
              <label>
                <span>Location</span>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Meerut"
                />
              </label>
              <label>
                <span>Tags</span>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="tag1, tag2"
                />
              </label>
            </div>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleInputChange}
              />
              <span>Publish immediately</span>
            </label>

            <div className="admin-other-news-form-actions">
              <button type="submit" className="primary-button" disabled={loading || pageLoading}>
                {loading ? 'Saving...' : editingId ? 'Update Other News' : 'Save Other News'}
              </button>
              {editingId ? (
                <button type="button" className="secondary-button" onClick={resetForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </form>
        </section>

        <section className="admin-card">
          <h2>Saved News</h2>
          {pageLoading ? (
            <div className="empty-state">Loading...</div>
          ) : newsList.length === 0 ? (
            <div className="empty-state">Abhi tak koi data add nahi hua.</div>
          ) : (
            <div className="admin-news-list">
              {newsList.map((item) => (
                <article key={item._id} className="admin-news-item">
                  <div className="admin-news-item-body">
                    <h3>{item.title}</h3>
                    <p>{item.menuTitle} / {item.submenu}</p>
                    <small>
                      {item.location ? `${item.location} | ` : ''}
                      {new Date(item.createdAt).toLocaleDateString('hi-IN')}
                    </small>
                  </div>
                  <div className="admin-news-item-actions">
                    <span className={`status-pill ${item.isPublished ? 'published' : 'draft'}`}>
                      {item.isPublished ? 'Published' : 'Draft'}
                    </span>
                    <button type="button" className="secondary-button" onClick={() => handleEdit(item)}>
                      Edit
                    </button>
                    <button type="button" className="danger-button" onClick={() => handleDelete(item._id)}>
                      Delete
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
          </section>
        </div>
      </div>
    </AdminShell>
  );
}

export default AdminOtherNews;
